import { Injectable } from '@angular/core';

import {
  DiagnosticsStackDeviceMainStructure,
  DiagnosticsStackGenericResponse,
  DiagnosticsStackLogicalLinkStructure,
  DiagnosticsStackLogicalLinkStructureDetails,
  RemoteDiagnosticsGetVddListByComChannelGeneric,
} from '@vi-iot/client';

import {
  ActiveErrorWithDiagStackDetail,
  CurrentProductMatrix,
  ProductMatrix,
  ProductMatrixStructure,
  RemoteDiagnosticsControlUnit,
  RemoteDiagnosticsVddListParameter,
} from '../remote-diagnostics/remote-diagnostics.model';

const busTopologyPhaseOne = 'BusTopologyPhaseOne';
const listOfVDDsByBaseVariant = 'ListOfVDDsByBaseVariant';

// topology ecu structure kinds
const enum ecuKinds {
  mainECU = 'Main ECU',
  additionalEcu = 'Additional ECUs',
  accessoriesEcu = 'Accessories',
  offlineEcu = 'OfflineECUs',
}

enum STR_36ByteStrucBusIdent {
  PA_BusAddre = 'PA_BusAddre',
  PA_BusType = 'PA_BusType',
  PA_DevicObjecPrope = 'PA_DevicObjecPrope',
  PA_DevicFunct = 'PA_DevicFunct',
}

@Injectable({
  providedIn: 'root',
})
export class RemoteDiagnosticsMappers {
  vddListExtractor(
    listOfVDDs: RemoteDiagnosticsGetVddListByComChannelGeneric
  ): RemoteDiagnosticsVddListParameter[] {
    if (listOfVDDs?.type === listOfVDDsByBaseVariant) {
      const resultVddList: RemoteDiagnosticsVddListParameter[] = [];

      listOfVDDs.structure.map((structure1) =>
        structure1.structure.map((structure2) => {
          resultVddList.push({
            name: structure2.texttableentry,
            value: structure2.TI_texttableentry,
            vddInterval: structure2.interval,
            isWrite: structure2?.isWrite === 'true',
            isRead: structure2?.isRead === 'true',
          });
        })
      );

      return resultVddList;
    }

    return [];
  }

  topologyControlUnitsExtractor(
    topology: DiagnosticsStackGenericResponse<DiagnosticsStackDeviceMainStructure[]>
  ): RemoteDiagnosticsControlUnit[] {
    if (topology?.type === busTopologyPhaseOne) {
      const controlUnits: RemoteDiagnosticsControlUnit[] = [];
      topology.structure.map((structure1) =>
        structure1.structure.map((structure2: DiagnosticsStackDeviceMainStructure) => {
          // here i am casting to DiagnosticsStackDeviceMainStructure, other structure types are ignored
          if (structure2?.name?.length) {
            structure2.structure.map((structure3: DiagnosticsStackLogicalLinkStructure) => {
              if (structure3.isknownlink && structure3.isonlinelink && structure3.structure) {
                let controlUnit: RemoteDiagnosticsControlUnit;
                structure3.structure.map(
                  (structure4: DiagnosticsStackLogicalLinkStructureDetails) =>
                    (controlUnit = this.getControlUnitFromTopologyStructure(structure4.structure))
                );
                controlUnit.value = structure3.logicallinkname;
                controlUnits.push(controlUnit);
              }
            });
          }
        })
      );

      const uniqeControlUnits = controlUnits.filter(
        (controlUnit, index) =>
          controlUnits.findIndex((cu) => cu.value === controlUnit.value) === index
      );

      return uniqeControlUnits.filter((cu) => !!cu);
    }

    return [];
  }

  topologyMainEcuExtractor(
    topology: DiagnosticsStackGenericResponse<DiagnosticsStackDeviceMainStructure[]>
  ): string {
    let foundLogicalLink = '';
    if (topology?.type === busTopologyPhaseOne) {
      topology.structure.forEach((structure1) =>
        structure1.structure.forEach((structure2: DiagnosticsStackDeviceMainStructure) => {
          if (structure2?.name === ecuKinds.mainECU) {
            structure2.structure.forEach((structure3: DiagnosticsStackLogicalLinkStructure) => {
              if (structure3?.logicallinkname) {
                foundLogicalLink = structure3.logicallinkname;
              }
            });
          }
        })
      );
    }

    return foundLogicalLink;
  }

  extractBundleId(
    topology: DiagnosticsStackGenericResponse<DiagnosticsStackDeviceMainStructure[]>
  ): string {
    let bundleId = '';
    topology.structure.forEach((structure1) => {
      const structureWithBundleId: DiagnosticsStackDeviceMainStructure = structure1.structure.find(
        (structure2: DiagnosticsStackDeviceMainStructure) => !!structure2.bundleID
      );
      if (structureWithBundleId) {
        bundleId = structureWithBundleId.bundleID;
      }
    });

    return bundleId;
  }

  topologyBusDetailsExtractor(
    topology: DiagnosticsStackGenericResponse<DiagnosticsStackDeviceMainStructure[]>
  ): ActiveErrorWithDiagStackDetail[] {
    let busDetailsWithLogicalLinks: ActiveErrorWithDiagStackDetail[] = [];

    if (topology?.type === busTopologyPhaseOne) {
      topology.structure.forEach((structure1) =>
        structure1.structure.forEach((structure2: DiagnosticsStackDeviceMainStructure) => {
          if (
            structure2?.name === ecuKinds.mainECU ||
            structure2?.name === ecuKinds.additionalEcu ||
            structure2?.name === ecuKinds.accessoriesEcu ||
            structure2?.name === ecuKinds.offlineEcu // probably can be removed - ask Grzegorz Gwóźdź
          ) {
            structure2?.structure?.forEach((structure3: DiagnosticsStackLogicalLinkStructure) => {
              structure3?.structure?.forEach(
                (structure4: DiagnosticsStackLogicalLinkStructureDetails) => {
                  const PA_BusAddre = structure4?.structure?.find(
                    (obj: DiagnosticsStackLogicalLinkStructureDetails) =>
                      obj?.name === 'PA_BusAddre'
                  )?.value;
                  const PA_BusType = structure4?.structure?.find(
                    (obj: DiagnosticsStackLogicalLinkStructureDetails) => obj?.name === 'PA_BusType'
                  )?.value;

                  const busDetailsWithLogicalLink: ActiveErrorWithDiagStackDetail = {
                    PA_BusAddre,
                    PA_BusType,
                    logicalLink: structure3.logicallinkname,
                    errorCode: null,
                  };
                  busDetailsWithLogicalLinks.push(busDetailsWithLogicalLink);
                }
              );
            });
          }
        })
      );
    }

    return busDetailsWithLogicalLinks;
  }

  productMatrixMapper(productMatrixResponse: ProductMatrixStructure): CurrentProductMatrix[] {
    if (productMatrixResponse.structure) {
      return productMatrixResponse.structure
        .filter(
          (structure) =>
            'ViessmannIdentificationNumber' in structure && 'logicalLinkName' in structure
        )
        .map((structure: ProductMatrix) => {
          const productMatrix: CurrentProductMatrix = {
            logicalLinkName: structure.logicalLinkName,
            viessmannIdentificationNumber: structure.ViessmannIdentificationNumber,
          };

          return productMatrix;
        });
    }

    const currentProductMatrix: CurrentProductMatrix = {
      logicalLinkName: productMatrixResponse.logicalLinkName,
      viessmannIdentificationNumber: productMatrixResponse.ViessmannIdentificationNumber,
    };

    return [currentProductMatrix];
  }

  getBusAddressOfLogicalLink(controlUnit: RemoteDiagnosticsControlUnit): string {
    const splitedControlUnit = controlUnit.value.split('_');
    const filteredValue = splitedControlUnit.filter((v) => v.startsWith('CAN'));

    if (filteredValue?.length > 1) {
      const busAddresses = filteredValue.map((v) => v.replace('CAN', ''));

      return busAddresses.join(' -> ');
    } else {
      return controlUnit.busAddress;
    }
  }

  private getControlUnitFromTopologyStructure(
    structure: DiagnosticsStackLogicalLinkStructureDetails[]
  ): RemoteDiagnosticsControlUnit {
    let controlUnit: RemoteDiagnosticsControlUnit = {
      value: null,
      busAddress: null,
      objectProperty: null,
      busType: null,
    };

    structure.forEach((s) => {
      const structureBusIdentificator = this.getAllAfterLastOccurenceOfChar(s?.path, '/');
      if (structureBusIdentificator === STR_36ByteStrucBusIdent.PA_DevicObjecPrope) {
        controlUnit.objectProperty = s?.TI_texttableentry;
      }
      if (structureBusIdentificator === STR_36ByteStrucBusIdent.PA_BusType) {
        controlUnit.busType = s?.TI_texttableentry;
      }
      if (structureBusIdentificator === STR_36ByteStrucBusIdent.PA_BusAddre) {
        controlUnit.busAddress = s?.value;
      }
    });

    return controlUnit;
  }

  private getAllAfterLastOccurenceOfChar(s: string, char: string): string {
    return s ? s.substring(s.lastIndexOf(char) + 1) : '';
  }
}
